import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import Styles from './AIProjectPlan.module.scss';
import ProjectPlanCard from './ProjectPlanCard';
import {
  generateBriefs,
  generateHeadlines,
  saveBriefs,
  updateProject,
} from 'services/projectService';
import { error as errorToast, success as successToast } from 'helpers/toaster';
import { sumDaysToDate, calculateStrategicDeadline } from 'helpers/dates';
import { ROUTES } from 'constants/routes';
import AIPlanLayout from './AIPlanLayout';
import Spinner from '../../Spinner/Spinner';
import BriefDrawer from 'Components/Drawers/BriefDrawer/BriefDrawer';
import ConfirmDeleteModal from 'Containers/Modal/ConfirmDeleteModal/ConfirmDeleteModal';
import { PROJECT_TABS } from 'constants/projects';
import { resetOnboarding } from 'redux/slices/newOnboarding';
import { useDispatch, useSelector } from 'react-redux';
import { success } from 'helpers/toaster';
import { actions } from 'redux/slices/aiProject';
import { useQueryClient } from '@tanstack/react-query';
import keys from 'constants/queryKeys';

const sanitizeBrief = ({ brief, projectId, index, totalBriefs, startDate, endDate, distributionDates }) => {
  let deadline;

  // If user selected specific distribution dates, use them
  if (distributionDates && distributionDates.length > 0) {
    // Sort dates chronologically
    const sortedDates = [...distributionDates].sort((a, b) => a - b);

    // Assign date based on index (cycle through if more briefs than dates)
    deadline = sortedDates[index % sortedDates.length];
  } else {
    // Fall back to strategic deadline calculation
    deadline = calculateStrategicDeadline({
      startDate,
      endDate,
      index,
      totalBriefs,
    });
  }

  return {
    id: uuid(),
    title: brief['Topic/Headline'],
    description: brief.Description,
    deadline_date: deadline,
    priority: 'medium',
    state: 'not_started',
    content_type: brief['Content Type'],
    keywords: brief['Keywords to Use'],
    project_id: projectId,
    content_structure: brief['Content Structure'],
    word_count: brief['Word Count'],
  };
};

const AIProjectPlan = ({ ...props }) => {
  // Destructure props and URL parameters
  const {
    headlines: contentTopics,
    subscriptionPlansDrawer,
    onboardingFooter,
    campaignStartDate,
    campaignEndDate,
    distributionDates,
  } = props ?? {};
  const { projectId } = useParams();
  const [headlines, setHeadlines] = useState(props.headlines || []);
  const [briefs, setBriefs] = useState([]);
  const [loadingBriefs, setLoadingBriefs] = useState([]);
  const [editBrief, setEditBrief] = useState();
  const [deleteTaskModalView, setDeleteTaskModalView] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false); // Auto-save flag
  const [campaignTitle, setCampaignTitle] = useState(null); // Store AI-generated campaign title
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const campaignCards = location?.state?.campaignCards;
  const customAmount = location?.state?.customAmount;
  const contentAmount = location?.state?.amount;
  const stateContentTypes = location?.state?.content_types; // Get content types from navigation state
  const replaceExisting = location?.state?.replaceExisting || false; // Flag to replace all existing briefs (regenerate mode)
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const isManual = query?.get('isManual') || 'true';
  const url = useSelector((state) => state?.newOnboarding?.step1?.url);
  const project = useSelector((state) => state?.aiProject);

  // Helper to mark a brief as done loading
  const setLoadingToFalse = (index) => {
    setLoadingBriefs((prev) => {
      const loadingArray = [...prev];
      loadingArray[index] = false;
      return loadingArray;
    });
  };

  // Fetch briefs based on generated headlines
  const fetchBriefs = () => {
    if (!headlines?.length) {
      return;
    }
    headlines.forEach((headline, index) => {
      setLoadingBriefs((prev) => {
        prev.push(true);
        return prev;
      });
      const generateBriefsParams = {
        project_id: projectId,
        headlines: [headline],
        business_id: props?.brand,
      };

      if (props?.flow === 'onboarding' || props?.flow === 'newPlan') {
        generateBriefsParams.url = url;
      }

      generateBriefs(generateBriefsParams)
        .then((res) => {
          if (!res) {
            setLoadingToFalse(index);
            return;
          }
          // Get dates from campaign props (for expandPlan) or project state
          // For expand plan: use today as start if campaign already started
          const today = new Date();
          const campaignStart = new Date(campaignStartDate || project.start_date || project.startDate || today);
          const startDate = today > campaignStart ? today : campaignStart;

          // For end date: extend if in past or within 7 days
          const campaignEnd = new Date(campaignEndDate || project.end_date || project.endDate || sumDaysToDate(30));
          const oneWeekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          const endDate = campaignEnd < oneWeekFromNow ? oneWeekFromNow : campaignEnd;

          const newBrief = sanitizeBrief({
            brief: res.data[0],
            projectId,
            index,
            totalBriefs: headlines.length,
            startDate,
            endDate,
            distributionDates,
          });
          setBriefs((prevBriefs) => [...prevBriefs, newBrief]);
          setLoadingToFalse(index);
        })
        .catch((e) => {
          setLoadingToFalse(index);
          console.error(e);
        });
    });
  };

  // Fetch headlines for the project plan
  const fetchHeadlines = async () => {
    try {
      setLoadingBriefs([]);

      // Determine the amount to generate - use contentAmount if available and customAmount is true
      const amountToGenerate = customAmount && contentAmount ? contentAmount : (props.amount || 30);

      // Determine content types - prioritize state content types, then props, then let backend use project defaults
      const contentTypesToUse = stateContentTypes || props?.headlines?.content_types;

      const generateHeadlinesParams = {
        project_id: projectId,
        amount: amountToGenerate,
        previously_generated: briefs.map(({ title, content_type }) => ({
          'Topic/Headline': title,
          'Content Type': content_type,
        })),
        start_date: props?.headlines?.start_date,
        end_date: props?.headlines?.end_date,
        business_id: props?.brand,
        topics: props?.topics,
        goals: props?.goals,
      };

      // Add campaign_id for ExpandPlan feature (when expanding existing campaign)
      if (props?.campaignId) {
        generateHeadlinesParams.campaign_id = props.campaignId;
      }

      // Only include content_types if we have them - otherwise let backend use project.content_types
      if (contentTypesToUse) {
        // If it's a string, split it into an array
        generateHeadlinesParams.content_types = typeof contentTypesToUse === 'string'
          ? contentTypesToUse.split(', ')
          : contentTypesToUse;
      }

      if (props?.flow === 'onboarding' || props?.flow === 'newPlan') {
        generateHeadlinesParams.url = url;
      }

      const resGenerateHeadlines = await generateHeadlines(
        generateHeadlinesParams,
      );

      // Handle new response structure with campaign_title
      const responseData = resGenerateHeadlines?.data;

      // Check if response has campaign_title and briefs (new format)
      if (responseData?.campaign_title && responseData?.briefs) {
        // If this is a new campaign from dashboard (newPlan flow) and we have a campaign title, update it immediately
        if (props?.flow === 'newPlan' && projectId && responseData.campaign_title) {
          // Update UI immediately
          setCampaignTitle(responseData.campaign_title);

          // CRITICAL: Wait for database update to complete before proceeding
          try {
            await updateProject({
              projectId,
              body: { name: responseData.campaign_title },
            });
            // Update Redux store after successful update
            dispatch(actions.setProject({ ...project, name: responseData.campaign_title }));
            // Invalidate React Query cache so ProjectDetail fetches fresh data
            queryClient.invalidateQueries({ queryKey: keys.Project(projectId) });
          } catch (updateError) {
            console.error('Failed to update campaign title:', updateError);
          }
        }

        // Extract briefs array for setting headlines (this triggers brief generation)
        setHeadlines(responseData.briefs);
      } else {
        // Backward compatibility: handle old format (direct array)
        setHeadlines(responseData);
      }
    } catch (e) {
      setLoadingBriefs([false]);
      errorToast(e);
    }
  };

  // Clear onboarding state after plan is accepted
  const clearOnboarding = () => {
    setTimeout(() => {
      dispatch(resetOnboarding());
    }, 5000); // 5 seconds
  };

  // Accept plan with an optional callback based on flow type
  const handleAcceptPlanWithCallback = () => {
    if (props.onHide) {
      props.onHide();
    }
    const callback =
      props.flow === 'newPlan'
        ? clearOnboarding
        : props.flow === 'onboarding'
          ? subscriptionPlansDrawer
          : props.flow === 'expandPlan'
            ? props.callback
            : null;
    onAcceptPlan(callback);
  };

  useEffect(() => {
    fetchHeadlines();
  }, []);

  useEffect(() => {
    fetchBriefs();
  }, [headlines]);

  // Determine if briefs are still loading.
  const isLoading = loadingBriefs.length === 0 || loadingBriefs.some((v) => v);

  // Auto-save useEffect: automatically trigger saveBriefs when briefs are generated
  // BUT NOT for expandPlan flow - user needs to manually approve which briefs to add
  useEffect(() => {
    if (!autoSaved && !isLoading && briefs.length > 0 && props.flow !== 'expandPlan') {
      onAcceptPlan();
      setAutoSaved(true);
    }
  }, [autoSaved, isLoading, briefs]);

  const navigateToPlan = () => {
    navigate(
      projectId
        ? ROUTES.ProjectSlug(projectId, { tab: PROJECT_TABS.plan })
        : ROUTES.PLANNER,
    );
  };

  // Save the briefs via the saveBriefs service
  const onAcceptPlan = async (callback) => {
    try {
      const briefsToSave = briefs.map((brief) => ({
        ...brief,
        assignee_id: brief.assignee?.id,
      }));

      let response = await saveBriefs({
        project_id: projectId,
        tasks: briefsToSave,
        replace_existing: replaceExisting, // If true, backend will delete existing briefs first
      });
      if (response.status === 200) {
        // Show success notification based on flow type
        if (props.flow === 'expandPlan') {
          const count = briefsToSave.length;
          success(`${count} ${count === 1 ? 'brief' : 'briefs'} added to plan successfully!`);
        } else if (replaceExisting) {
          const count = briefsToSave.length;
          success(`Campaign regenerated with ${count} ${count === 1 ? 'brief' : 'briefs'}!`);
        }

        props.flow !== 'onboarding' && navigateToPlan();
        if (callback) {
          callback();
        }
      } else {
        errorToast('An error occurred while saving briefs.');
      }
    } catch (e) {
      errorToast(e);
    }
  };

  const onDeleteHandler = () => {
    setDeleteTaskModalView(true);
  };

  const onConfirmDeleteHandler = async () => {
    setBriefs((prevBriefs) =>
      prevBriefs.filter((brief) => brief.id !== editBrief?.id),
    );
    setEditBrief();
  };

  const handleRemoveBrief = (briefToRemove) => {
    setBriefs((prevBriefs) =>
      prevBriefs.filter((brief) => brief.id !== briefToRemove.id),
    );
  };

  return (
    <>
      <AIPlanLayout
        loading={isLoading}
        onBoostPlan={fetchHeadlines}
        onAcceptPlan={handleAcceptPlanWithCallback}
        onCancel={navigateToPlan}
        isManual={isManual}
        campaignTitle={campaignTitle}
        header={
          (props.flow === 'onboarding' || props.flow === 'expandPlan') &&
          'Happy with your initial plan?'
        }
        description={
          props.flow === 'expandPlan' &&
          'Review the generated briefs and remove any you don\'t want. Click "Add to Plan" when ready.'
        }
        buttonText={
          (props.flow === 'onboarding' || props.flow === 'expandPlan') &&
          'Add more'
        }
        showButton={
          props.flow !== 'onboarding' &&
          props.flow !== 'newPlan' &&
          props.flow !== 'expandPlan'
        }
        compactHeader={props.flow === 'expandPlan'}
        projectId={projectId}
      >
        <div className={Styles.cardsTable}>
          {briefs.map((brief, index) => (
            <ProjectPlanCard
              key={index}
              brief={brief}
              onClick={() => setEditBrief(brief)}
              onboardingFooter={onboardingFooter || campaignCards}
              onRemove={props.flow === 'expandPlan' ? handleRemoveBrief : undefined}
            />
          ))}
          {isLoading && <Spinner text="Generating content" className="mh-100" />}
        </div>
      </AIPlanLayout>

      {!!editBrief && (
        <BriefDrawer
          onHide={() => setEditBrief()}
          onSubmit={(action, values) => {
            setBriefs((prevBriefs) => {
              const indexOfBriefToReplace = prevBriefs.findIndex(
                (brief) => brief.id === editBrief?.id,
              );
              prevBriefs[indexOfBriefToReplace] = { ...editBrief, ...values };
              successToast('Brief Updated!');
              return prevBriefs;
            });
            setEditBrief();
          }}
          action="edit"
          task={editBrief}
          show={!!editBrief}
          onDeleteHandler={onDeleteHandler}
          disableCreateDraft
        />
      )}

      {deleteTaskModalView && (
        <ConfirmDeleteModal
          show={deleteTaskModalView}
          onHide={() => setDeleteTaskModalView(false)}
          onDelete={onConfirmDeleteHandler}
        />
      )}
    </>
  );
};

export default AIProjectPlan;
